import { useState, useEffect } from 'react';
import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { isCampaignBuilderPath, isDataDenseRoute, isFillViewportRoute } from './dataRoutePaths';
import TrialExpiredGate from '../components/TrialExpiredGate';
import PaymentHoldBanner from '../components/PaymentHoldBanner';
import LeadJobSalesHost from '../components/LeadJobSalesHost';
import TrialStatusBanner from '../components/TrialStatusBanner';
import FeedbackPrompt from '../components/support/FeedbackPrompt';
import { SubscriptionProvider } from '../providers/SubscriptionProvider';
import { WorkspaceProvider } from './WorkspaceContext';
import { useTeamStore } from '../stores/teamStore';

const AppShell = () => {
  const location = useLocation();
  const builderRoute = isCampaignBuilderPath(location.pathname, location.search);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(builderRoute);
  // Collapse in the same render that enters the builder, before anything paints. The builder's
  // own collapse event arrives after its first paint, which showed the sidebar opening then closing.
  const [wasBuilderRoute, setWasBuilderRoute] = useState(builderRoute);
  if (builderRoute !== wasBuilderRoute) {
    setWasBuilderRoute(builderRoute);
    if (builderRoute) setSidebarCollapsed(true);
  }

  const isSettingsRoute = location.pathname.startsWith('/app/settings');
  const isOdinRoute = location.pathname.startsWith('/app/odin');
  const isPaidSetupRoute = location.pathname.startsWith('/app/setup');

  // Load the user's teams once for the whole authenticated app. Only Settings,
  // the paid-setup panel and the voicemails page used to fetch them, so both
  // the team picker and the team a new campaign is created into depended on
  // whether the user happened to visit one of those first. Selector form keeps
  // the shell from re-rendering on unrelated team state changes.
  const fetchTeams = useTeamStore((s) => s.fetchTeams);
  useEffect(() => {
    void fetchTeams();
  }, [fetchTeams]);

  // Persist and adapt sidebar collapse based on screen size
  useEffect(() => {
    const persisted = localStorage.getItem('ui_sidebar_collapsed');

    const applyResponsiveState = () => {
      const width = window.innerWidth;
      const isSmall = width < 1024;
      const isDataRoute = isDataDenseRoute(location.pathname);
      const isLaptopDataViewport = isDataRoute && width >= 1280 && width < 1440;

      if (isSmall || isLaptopDataViewport || isCampaignBuilderPath(window.location.pathname, window.location.search)) {
        setSidebarCollapsed(true);
      } else if (persisted !== null) {
        setSidebarCollapsed(persisted === 'true');
      } else {
        setSidebarCollapsed(false);
      }
    };

    if (persisted !== null) {
      setSidebarCollapsed(persisted === 'true');
    }

    applyResponsiveState();
    window.addEventListener('resize', applyResponsiveState);
    return () => window.removeEventListener('resize', applyResponsiveState);
  }, [location.pathname]);

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('ui_sidebar_collapsed', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail === 'boolean') {
        setSidebarCollapsed(detail);
      } else if (detail === 'restore') {
        try {
          const persisted = localStorage.getItem('ui_sidebar_collapsed');
          setSidebarCollapsed(persisted === 'true');
        } catch {
          setSidebarCollapsed(false);
        }
      }
    };
    window.addEventListener('cursor:sidebar-collapse', handler as EventListener);
    return () => window.removeEventListener('cursor:sidebar-collapse', handler as EventListener);
  }, []);

  const isFullPageMode = isSettingsRoute || isOdinRoute || isPaidSetupRoute;
  const fillViewport = isFillViewportRoute(location.pathname);

  // Lock the document scrollingElement while AppShell is mounted so the
  // far-right browser scrollbar cannot scroll the whole app off-screen.
  // Page content still scrolls via <main> (scrollbar chrome hidden).
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const appRoot = document.getElementById('root');
    const prev = {
      htmlOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      rootOverflow: appRoot?.style.overflow ?? '',
      rootHeight: appRoot?.style.height ?? '',
    };
    root.classList.add('app-shell-scroll-lock');
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (appRoot) {
      appRoot.style.overflow = 'hidden';
      appRoot.style.height = '100%';
    }
    return () => {
      root.classList.remove('app-shell-scroll-lock');
      root.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      if (appRoot) {
        appRoot.style.overflow = prev.rootOverflow;
        appRoot.style.height = prev.rootHeight;
      }
    };
  }, []);

  return (
    <SubscriptionProvider>
      <WorkspaceProvider>
        <TrialExpiredGate />
        <LeadJobSalesHost />
        {isFullPageMode ? (
          <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-white">
            <PaymentHoldBanner />
            <div className="min-h-0 flex-1 overflow-hidden">
              <Outlet />
            </div>
          </div>
        ) : (
          <div className="flex h-[100dvh] max-h-[100dvh] overflow-hidden bg-white">
            <Sidebar collapsed={sidebarCollapsed} onToggle={handleToggleSidebar} />

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <Topbar
                onSidebarToggle={handleToggleSidebar}
                sidebarCollapsed={sidebarCollapsed}
              />
              <PaymentHoldBanner />
              {!builderRoute && (
                <div className="shrink-0 px-4 sm:px-6">
                  <TrialStatusBanner />
                </div>
              )}

              {!builderRoute && <FeedbackPrompt />}
              <main
                className={`min-h-0 min-w-0 flex-1 bg-white scrollbar-none max-lg:overflow-x-hidden ${
                  fillViewport ? 'overflow-hidden' : 'overflow-y-auto'
                }`}
              >
                <div
                  className={`flex min-w-0 flex-col ${
                    fillViewport ? 'h-full min-h-0 overflow-hidden' : 'min-h-full'
                  }`}
                >
                  <Outlet />
                </div>
              </main>
            </div>

          </div>
        )}
      </WorkspaceProvider>
    </SubscriptionProvider>
  );
};

export default AppShell;
